'use client';

import { useState, type KeyboardEvent } from 'react';
import { useLocale } from 'next-intl';
import { cn } from '@lib/utils';
import { TrackedAnchor } from '@/components/ancher';
import { useRouter } from '@/navigation';
import {
  TranslatedNavItem,
  TranslatedNavTree,
  useSiteNav,
} from '@/hooks/useMenu.hook';
import { HOVER_CLOSE_DELAY_MS, useMegaMenu } from './use-mega-menu';
import {
  MENUBAR_TRIGGER_ATTR,
  focusTriggerByKey,
  wrapIndex,
} from './menubar-keys';

// 시각적 trigger 박스(콘텐츠 너비 × 48) — hover 트리거 / 컬러 변경 / focus outline 모두 anchor 한정.
// 마우스 닫힘은 anchor 가 아니라 <nav> 영역이 소유 — anchor 사이 gap 을 지나도 닫히지 않는다.
const TRIGGER_BASE =
  'flex h-12 items-center justify-center whitespace-nowrap text-[16px] font-semibold leading-[24px] tracking-[-0.18px] outline-none transition-colors focus-visible:underline focus-visible:underline-offset-4';

interface MainNavTriggerProps {
  item: TranslatedNavItem;
  isFocused: boolean;
  onFocusedChange: () => void;
}

const MainNavTrigger = ({
  item,
  isFocused,
  onFocusedChange,
}: MainNavTriggerProps) => {
  const {
    activeMenuKey,
    openMenu,
    closeMenu,
    focusFirstPanelItem,
    focusLastPanelItem,
  } = useMegaMenu();
  const hasMegaMenu = !!item.groups?.length;
  const isActive = activeMenuKey === item.key;
  const router = useRouter();

  // 뷰포트 자동 프리페치(prefetch={false})는 끄고 hover/focus 시에만 워밍업 —
  // 헤더 nav 는 항상 뷰포트라 기본 auto 프리페치가 전 라우트 RSC(각 ~180KB)를 즉시 당겨 초기 대역폭을 키웠다.
  // 외부 링크(target 지정 / http)는 next-intl 라우터 프리페치 대상이 아니므로 제외.
  const warmPrefetch = () => {
    if (!item.target && !item.href.startsWith('http'))
      router.prefetch(item.href);
  };

  // hasMegaMenu trigger 자체 키: ↓/Space/Enter → submenu + 첫 anchor, ↑ → submenu + 마지막.
  // ←/→ 는 menubar-level keydown 이 처리 (ul 의 onKeyDown).
  //
  // requestAnimationFrame 으로 한 frame 늦추는 이유: openMenu 가 다른 key 로 메뉴 전환할 때
  // 새 panel content 는 다음 render commit 후에야 DOM 에 반영됨. 즉시 focus 호출하면
  // 이전 panel 의 anchor 가 잡혀 stale. 다음 paint 직전(commit+layout 완료 후) 실행되는
  // requestAnimationFrame 콜백으로 안전하게 새 panel 의 첫/마지막 anchor 에 접근.
  const handleKeyDown = (e: KeyboardEvent<HTMLAnchorElement>) => {
    if (!hasMegaMenu) return;
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openMenu(item.key);
      requestAnimationFrame(focusFirstPanelItem);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      openMenu(item.key);
      requestAnimationFrame(focusLastPanelItem);
    }
  };

  return (
    <li role="none" className="flex items-center">
      <TrackedAnchor
        label={item.gtagLabel}
        position="nav"
        href={item.href}
        target={item.target}
        rel={item.rel}
        role="menuitem"
        prefetch={false}
        tabIndex={isFocused ? 0 : -1}
        aria-haspopup={hasMegaMenu ? 'menu' : undefined}
        aria-expanded={hasMegaMenu ? isActive : undefined}
        {...{ [MENUBAR_TRIGGER_ATTR]: item.key }}
        onMouseEnter={() => {
          warmPrefetch();
          // plain link 은 메뉴가 없으므로 열려 있던 메가메뉴를 닫는다.
          if (hasMegaMenu) openMenu(item.key);
          else closeMenu();
        }}
        onFocus={() => {
          warmPrefetch();
          onFocusedChange();
          if (hasMegaMenu) openMenu(item.key);
        }}
        onBlur={() => {
          if (hasMegaMenu) closeMenu({ delay: HOVER_CLOSE_DELAY_MS });
        }}
        onKeyDown={handleKeyDown}
        className={cn(
          TRIGGER_BASE,
          isActive ? 'text-[#ff142d]' : 'text-[#040435] hover:text-[#ff142d]',
        )}
      >
        {item.label}
      </TrackedAnchor>
    </li>
  );
};

// menubar-level keydown: ArrowLeft/Right 로 trigger 간 이동 (wrap).
// 이벤트가 ul 에 bubble 되어 와서 e.target 의 data-menubar-trigger 로 현재 trigger 식별.
const handleMenubarKeyDown = (
  e: KeyboardEvent<HTMLUListElement>,
  items: TranslatedNavTree,
) => {
  if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
  const target = e.target as HTMLElement;
  const currentKey = target.getAttribute(MENUBAR_TRIGGER_ATTR);
  if (!currentKey) return;
  const idx = items.findIndex(item => item.key === currentKey);
  if (idx === -1) return;
  e.preventDefault();
  const offset = e.key === 'ArrowLeft' ? -1 : 1;
  focusTriggerByKey(items[wrapIndex(idx, offset, items.length)].key);
};

const MainNav = () => {
  const { createMenu } = useSiteNav();
  const { cancelDelayedClose, closeMenu } = useMegaMenu();
  const items = createMenu();
  const locale = useLocale();
  // roving tabindex — menubar 가 단일 tab stop 이고 활성 item 만 tabindex=0.
  const [focusedKey, setFocusedKey] = useState<string | undefined>(
    items[0]?.key,
  );

  return (
    // 마우스 닫힘 소유 — <nav> 를 벗어나면(로고·RightNav·헤더 밖) 지연 닫힘.
    <nav
      aria-label="Main navigation"
      className="relative h-full max-[1366px]:hidden"
      onMouseEnter={cancelDelayedClose}
      onMouseLeave={() => closeMenu({ delay: HOVER_CLOSE_DELAY_MS })}
    >
      <ul
        role="menubar"
        // gap: 디자이너 요청 EN 40px / KO 80px. [&_br]:hidden: 라벨의 <br>(풀메뉴 전용)을 바에선 숨김.
        className={cn(
          'flex h-full list-none [&_br]:hidden',
          locale === 'ko' ? 'gap-[80px]' : 'gap-[40px]',
        )}
        onKeyDown={e => handleMenubarKeyDown(e, items)}
      >
        {items.map(item => (
          <MainNavTrigger
            key={item.key}
            item={item}
            isFocused={focusedKey === item.key}
            onFocusedChange={() => setFocusedKey(item.key)}
          />
        ))}
      </ul>
    </nav>
  );
};

export default MainNav;
